export const VISIBILITY_CATALOG_FIELD = "visibilityKnownTemplateIds" as const;

type SceneData = Record<string, unknown>;

function assertTemplateIds(value: unknown, name: string): asserts value is string[] {
  if (!Array.isArray(value) || value.some((id) => typeof id !== "string")) {
    throw new Error(`${name} must be an array of template IDs`);
  }
}

/**
 * Record which templates existed when an admin saved Visibility.
 */
export function withVisibilityCatalog(
  scene: SceneData,
  currentTemplateIds: string[]
): SceneData {
  if (scene.visible === undefined) return scene;
  assertTemplateIds(scene.visible, "scene.visible");
  assertTemplateIds(currentTemplateIds, "currentTemplateIds");
  return {...scene, [VISIBILITY_CATALOG_FIELD]: [...currentTemplateIds]};
}

/**
 * Make templates introduced after a Visibility save visible by default.
 * A template absent from both `visible` and the saved catalog is new. A
 * template absent from `visible` but present in the catalog was hidden by an
 * admin and stays hidden.
 *
 * `slotTemplateIdsOf` gives the ids of the designs that share a template's
 * place on the page (data/templateSlots.ts). A new design of a place that the
 * catalog knows takes the state of the known design: visible when one of them
 * is visible, hidden when the admin hid them.
 */
export function materializeDefaultSceneVisibility<T extends SceneData | null | undefined>(
  scene: T,
  currentTemplateIds: string[],
  slotTemplateIdsOf?: (templateId: string) => readonly string[]
): T {
  if (!scene || scene.visible === undefined) return scene;
  assertTemplateIds(scene.visible, "scene.visible");

  const knownTemplateIds = scene[VISIBILITY_CATALOG_FIELD];
  // The deployment migration adds this field to legacy default-scene rows.
  // Until then, preserve their old meaning instead of guessing user intent.
  if (knownTemplateIds === undefined) return scene;

  assertTemplateIds(knownTemplateIds, `scene.${VISIBILITY_CATALOG_FIELD}`);
  assertTemplateIds(currentTemplateIds, "currentTemplateIds");

  const known = new Set(knownTemplateIds);
  const visible = new Set(scene.visible);
  let changed = false;
  for (const templateId of currentTemplateIds) {
    if (known.has(templateId) || visible.has(templateId)) continue;
    const knownSiblings = (slotTemplateIdsOf?.(templateId) ?? []).filter(
      (id) => id !== templateId && known.has(id)
    );
    if (knownSiblings.length > 0 && !knownSiblings.some((id) => visible.has(id))) continue;
    visible.add(templateId);
    changed = true;
  }

  return (changed ? {...scene, visible: [...visible]} : scene) as T;
}
